import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Breadcrumb, Col, Empty, Row, Timeline } from 'antd';
import { Button, SIZE, Stack } from '@quinyx/ui';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { ICreateCustomerProgress } from '../../../../api/interfaces/customer';
import Section from '../../../../components/Section';
import { useCustomerCreationProgress } from '../../../../hooks/customer';
import {
  CUSTOMERROUTE,
  withCustomerLayout,
} from '../../../../layouts/customer';
import { useTranslate } from '../../../../hooks/translate';

const CREATION_SUCCESS_MESSAGE = 'customer successfully created';

const Task = () => {
  const { t } = useTranslate();
  const { progressId } = useParams();
  const navigate = useNavigate();
  const { getCustomerCreationProgress } = useCustomerCreationProgress();
  const [progress, setProgress] = useState<
    ICreateCustomerProgress & { lastMessage?: string }
  >();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (progressId && typeof progressId === 'string') {
      const poll = async () => {
        const progressData = await getCustomerCreationProgress(progressId);
        if (!progressData) {
          return;
        }
        const lastMessage = progressData.messages.pop();
        setProgress({ lastMessage, ...progressData });
        const { currentStatus } = progressData;
        if (
          currentStatus === 'not started yet' ||
          currentStatus === 'in progress'
        ) {
          timeout = setTimeout(poll, 3000);
        }
      };
      poll();
    }
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const isPending =
    progress?.currentStatus === 'not started yet' ||
    progress?.currentStatus === 'in progress';

  return (
    <Row gutter={[16, 42]}>
      <Col span={24}>
        <Breadcrumb data-test-id="customer-create-in-progress-breadcrumb">
          <Breadcrumb.Item data-test-id="customer-create-in-progress-breadcrumb-item">
            <Link
              data-test-id="customer-create-in-progress-breadcrumb-link"
              to="/customers">
              {t('customer.customerList')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item data-test-id="customer-create-in-progress-breadcrumb-item">
            <Link
              data-test-id="customer-create-in-progress-breadcrumb-link"
              to={CUSTOMERROUTE.CREATE}>
              {t('customer.createNewCustomerBreadcrumb')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item data-test-id="customer-create-in-progress-breadcrumb-item">
            <Link
              data-test-id="customer-create-in-progress-breadcrumb-link"
              to={CUSTOMERROUTE.IN_PROGRESS}>
              {t('customer.creationQueue')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{progressId}</Breadcrumb.Item>
        </Breadcrumb>
      </Col>
      <Col sm={24} md={18} lg={12}>
        <Section title={t('customer.creationStatus')}>
          {progress ? (
            <>
              {isPending ? (
                <Timeline pending={progress.lastMessage}>
                  {progress.messages.map((message) => (
                    <Timeline.Item key={message} color="green">
                      {message}
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Timeline>
                  {progress.messages.map((message) => (
                    <Timeline.Item key={message} color="green">
                      {message}
                    </Timeline.Item>
                  ))}
                  <Timeline.Item
                    dot={
                      progress.currentStatus === 'done' ? (
                        <CheckCircleOutlined style={{ fontSize: '20px' }} />
                      ) : (
                        <ExclamationCircleOutlined
                          style={{ fontSize: '20px' }}
                        />
                      )
                    }
                    color={progress.currentStatus === 'done' ? 'green' : 'red'}>
                    {progress.lastMessage}
                  </Timeline.Item>
                </Timeline>
              )}
              {progress.currentStatus === 'failed' ? (
                <>
                  <p>
                    {t('customer.creationFailedAtStep')}{' '}
                    <b>
                      <i>{progress.lastMessage}</i>
                    </b>
                  </p>
                  <p>
                    {t('customer.creationFailedTraceId')}{' '}
                    <b>
                      <i>{progress.traceId}</i>
                    </b>
                  </p>
                </>
              ) : (
                <p>{t('customer.creationInProgressNote')}</p>
              )}
            </>
          ) : (
            <Empty />
          )}
        </Section>
      </Col>
      <Col span={24}>
        <Stack direction="row" spacing={SIZE.S}>
          <Button
            data-test-id="back-button"
            onClick={() => navigate('/customers/create/in-progress')}
            text={t('common.back')}
            variant="text"
          />
          {progress?.lastMessage === CREATION_SUCCESS_MESSAGE && (
            <Button
              data-test-id="create-license-button"
              onClick={() =>
                navigate(`/customers/${progress.domainId}/#license`)
              }
              text={t('customer.createLicenseAfterCreation')}
            />
          )}
        </Stack>
      </Col>
    </Row>
  );
};

export default withCustomerLayout(Task);
